/**
 * GpuProfilerAggregator — rolling-average aggregator for GPU pass timings.
 *
 * Consumes per-frame nanosecond measurements from an IGpuTimerBackend and
 * maintains a rolling window of N frames so the UI overlay shows stable
 * millisecond values rather than frame-to-frame jitter.
 *
 * Wall-clock frame duration (for total GPU frame time estimation) uses
 * `performance.now()` — this is the one place where CPU time is intentional
 * since we are measuring overall frame wall time, not individual pass GPU time.
 *
 * Usage:
 *   const agg = new GpuProfilerAggregator(backend, 16);
 *
 *   // Each frame:
 *   agg.submitFrame(backend.readFrame());
 *
 *   // Query stable averages:
 *   agg.getAverageMs('ShadowPass'); // → 0.42
 *   agg.getAllPasses();              // → ['MainScene', 'ShadowPass', ...]
 */
import type { IGpuTimerBackend } from './GpuTimerBackend.js';
import type { GpuTimingSource, PassStats } from './GpuTimingSource.js';

const NS_TO_MS = 1e-6;

interface PassHistory {
  /** Circular ring buffer of raw nanosecond samples. */
  samples: Float64Array;
  /** Write pointer into samples. */
  head: number;
  /** How many valid samples are in the buffer (0..windowSize). */
  count: number;
  /** Running sum for O(1) average. */
  sum: number;
  /** Last single-frame value in ms (for UI raw display). */
  lastMs: number;
}

export class GpuProfilerAggregator implements GpuTimingSource {
  private readonly _backend: IGpuTimerBackend;
  private readonly _windowSize: number;
  private readonly _passes = new Map<string, PassHistory>();

  /**
   * @param backend    The timer backend to pull frames from.
   * @param windowSize Rolling average window in frames (default 16).
   */
  constructor(backend: IGpuTimerBackend, windowSize = 16) {
    this._backend = backend;
    this._windowSize = Math.max(1, windowSize);
  }

  /**
   * True when the wrapped backend can time GPU work.
   * Part of the GpuTimingSource contract.
   */
  get available(): boolean {
    return this._backend.available;
  }

  /**
   * Submit a completed frame's timing map (in nanoseconds) to the aggregator.
   * Call this once per frame with the result of `backend.readFrame()`.
   *
   * @param frame  Timing map from IGpuTimerBackend.readFrame().
   */
  submitFrame(frame: ReadonlyMap<string, number>): void {
    for (const [name, ns] of frame) {
      this._record(name, ns);
    }
  }

  /**
   * Convenience: read from the backend and submit in one call.
   * Equivalent to `submitFrame(backend.readFrame())`.
   */
  tick(): void {
    this.submitFrame(this._backend.readFrame());
  }

  /**
   * Returns the rolling-average GPU time in milliseconds for a named pass.
   * Returns 0 if the pass has never been recorded.
   */
  getAverageMs(name: string): number {
    const h = this._passes.get(name);
    if (!h || h.count === 0) return 0;
    return (h.sum / h.count) * NS_TO_MS;
  }

  /**
   * Returns the most recent single-frame GPU time in milliseconds.
   * Returns 0 if the pass has never been recorded.
   */
  getFrameMs(name: string): number {
    return this._passes.get(name)?.lastMs ?? 0;
  }

  /**
   * GpuTimingSource view over the aggregated windows.
   *
   * The backend reports NANOSECONDS (IGpuTimerBackend contract); PassStats is
   * milliseconds, so every value is scaled by 1e-6 (NS_TO_MS) exactly once,
   * inside `_historyMs`.
   */
  getFrameStats(): Map<string, PassStats> {
    const out = new Map<string, PassStats>();
    if (!this._backend.available) return out;
    for (const name of this.getAllPasses()) {
      const history = this._historyMs(name);
      if (history.length === 0) continue;
      let min = Infinity;
      let max = 0;
      for (const v of history) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
      out.set(name, {
        meanMs: this.getAverageMs(name),
        maxMs: max,
        minMs: min,
        lastMs: history[history.length - 1],
        history,
      });
    }
    return out;
  }

  /**
   * Returns all pass names that have been recorded at least once,
   * sorted alphabetically for stable UI ordering.
   */
  getAllPasses(): string[] {
    return Array.from(this._passes.keys()).sort();
  }

  /**
   * Returns the number of valid samples stored for a given pass.
   * Useful for warming-up indicators (< windowSize = still filling).
   */
  getSampleCount(name: string): number {
    return this._passes.get(name)?.count ?? 0;
  }

  /**
   * Clear all accumulated history. Useful when the scene or pass set changes.
   */
  reset(): void {
    this._passes.clear();
  }

  // ── Private helpers ──────────────────────────────────────────────────

  /**
   * Read a pass's ring buffer in chronological order (oldest first),
   * converted to milliseconds. Reads the SAME `samples` storage `getAverageMs`
   * sums over — no second copy of the sample window.
   *
   * `samples` is a circular buffer: `head` is the next write slot, which
   * (once the window is full) is also the oldest surviving sample. The most
   * recent `count` writes therefore live at positions
   * `(head - count + i) % windowSize` for i in [0, count).
   */
  private _historyMs(name: string): number[] {
    const h = this._passes.get(name);
    if (!h || h.count === 0) return [];
    const windowSize = h.samples.length;
    const out: number[] = new Array(h.count);
    for (let i = 0; i < h.count; i++) {
      const idx = (((h.head - h.count + i) % windowSize) + windowSize) % windowSize;
      out[i] = h.samples[idx] * NS_TO_MS;
    }
    return out;
  }

  private _record(name: string, ns: number): void {
    let h = this._passes.get(name);
    if (!h) {
      h = {
        samples: new Float64Array(this._windowSize),
        head: 0,
        count: 0,
        sum: 0,
        lastMs: 0,
      };
      this._passes.set(name, h);
    }

    // Evict oldest sample when window is full
    if (h.count === this._windowSize) {
      h.sum -= h.samples[h.head];
    } else {
      h.count++;
    }

    h.samples[h.head] = ns;
    h.sum += ns;
    h.head = (h.head + 1) % this._windowSize;
    h.lastMs = ns * NS_TO_MS;
  }
}
